/* eslint-disable radar/cognitive-complexity */
import {
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react';

import { HTMLMotionProps } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { BiCertification, BiInfoCircle, BiShield } from 'react-icons/bi';
import { useTheme } from 'styled-components';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

import { api } from '@services';
import { DEFAULT_MODAL_ANIMATION, DEFAULT_TRANSITION } from '@constants';
import { Button } from '@components';
import { useAuth, useEventfy } from '@hooks';
import { selectUser } from '@hooks/useAuth/selectors';

import * as S from './styles';

type CertificateModalBaseProps = HTMLMotionProps<'dialog'>;

type CertificateModalProps = PropsWithChildren<CertificateModalBaseProps>;

const CertificateModal = (
  props: CertificateModalProps,
  ref: ForwardedRef<HTMLDialogElement>,
) => {
  const theme = useTheme();
  const event = useEventfy();
  const { name } = useAuth(selectUser);
  const { t } = useTranslation();

  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { children, ...attrs } = props;

  const requiresPassword = event.certificate_password_required === 1;

  const handleDownloadCertificate = useCallback(async () => {
    if (requiresPassword && !password) {
      toast.error('Por favor, digite a senha do certificado');
      return;
    }

    try {
      setIsLoading(true);

      const response = await api.post<Blob>(
        '/user/certificate',
        { password },
        {
          responseType: 'blob',
        },
      );

      const url = URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Certificado ${name} - ${event.name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      toast.success('Certificado baixado com sucesso!');
    } catch (err) {
      const fallbackMessage = 'Erro ao baixar certificado';
      const message =
        (err as AxiosError<{ message: string }>).response?.data.message ??
        fallbackMessage;
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [requiresPassword, password, name, event.name]);

  return (
    <S.Container
      open
      ref={ref}
      transition={DEFAULT_TRANSITION}
      variants={DEFAULT_MODAL_ANIMATION}
      {...attrs}
    >
      {children}

      <h2>{t('modal.certificate.title')}</h2>

      <S.Wrapper>
        <div>
          {event.image_certificate ? (
            <img
              src={event.image_certificate}
              alt={t('modal.certificate.title')}
            />
          ) : (
            <BiCertification size="8rem" color={theme.colors.success[100]} />
          )}

          <div>
            <S.TitleText>Baixar Certificado</S.TitleText>
            <S.SubtitleText>
              {requiresPassword
                ? 'Parabéns por concluir o evento! Digite sua senha para validar e baixar seu certificado de participação.'
                : 'Parabéns por concluir o evento! Seu certificado de participação está pronto.'}
            </S.SubtitleText>
          </div>

          {requiresPassword && (
            <S.PasswordSection>
              <S.PasswordLabel htmlFor="certificate-password">
                Senha de Validação
              </S.PasswordLabel>
              <S.PasswordInput
                id="certificate-password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite sua senha"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && password) {
                    handleDownloadCertificate();
                  }
                }}
              />
              <S.PasswordHint>
                <BiInfoCircle size={14} />A senha foi informada durante a
                transmissão.
              </S.PasswordHint>

              <S.InfoBox>
                <BiShield size={20} color={theme.colors.warning[100]} />
                <p>
                  Seu certificado será emitido com base no nome informado em seu
                  cadastro na plataforma.
                </p>
              </S.InfoBox>
            </S.PasswordSection>
          )}

          <S.ButtonWrapper>
            <Button
              title={
                requiresPassword
                  ? 'Validar e Baixar Certificado'
                  : 'Baixar Certificado'
              }
              onClick={handleDownloadCertificate}
              loading={isLoading}
              disabled={isLoading}
            />
          </S.ButtonWrapper>
        </div>
      </S.Wrapper>
    </S.Container>
  );
};

export default forwardRef(CertificateModal);
